import { useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ReCAPTCHA from 'react-google-recaptcha';
import { z } from 'zod';

import facebookIcon from '../assets/facebook.png';
import saltBg from '../assets/salt-bg.jpg';
import tripadvisorIcon from '../assets/tripadvisor.png';
import { useTranslations } from '../i18n';

const FORM_SUBMISSION_ENDPOINT = 'https://formsubmit.co/ajax/check_charly@abv.bg';

declare global {
  interface Window {
    grecaptcha?: {
      getResponse: () => string;
      reset: () => void;
    };
  }
}

type ContactValidationMessages = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  captcha: string;
};

type ContactSubmissionCopy = {
  success: string;
  error: string;
  sending: string;
  cta: string;
  captchaMissing?: string;
  subjectTemplate?: string;
  notProvided?: string;
};

type ContactFieldLabels = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
};

type ContactPlaceholders = {
  name: string;
  email: string;
  phone: string;
  message: string;
  subject?: string;
};

type ContactContent = {
  heroHeading: string;
  heroSubheading: string;
  fields: ContactFieldLabels;
  placeholders: ContactPlaceholders;
  validation: ContactValidationMessages;
  submission: ContactSubmissionCopy;
};

function createContactSchema(messages: ContactValidationMessages) {
  return z.object({
    name: z.string().min(2, messages.name),
    email: z.string().email(messages.email),
    phone: z
      .string()
      .trim()
      .optional()
      .refine((value) => !value || /^\+?[0-9 ()-]{7,18}$/.test(value), messages.phone),
    subject: z.string().trim().min(3, messages.subject),
    message: z.string().min(10, messages.message),
  });
}

type ContactFormValues = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
};

export function ContactPage() {
  const { t } = useTranslations();
  const location = useLocation();
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? '';
  const isRecaptchaConfigured = Boolean(recaptchaSiteKey);

  useEffect(() => {
    if (location.hash !== '#contact-form') {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const formElement = document.getElementById('contact-form');
      if (!formElement) {
        return;
      }

      const offsetTop = formElement.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
      window.requestAnimationFrame(() => {
        const firstField = formElement.querySelector<HTMLElement>('input, textarea, select');
        firstField?.focus();
      });
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [location.hash]);

  const contactContent = useMemo(() => t('contact', {}) as ContactContent, [t]);
  const contactSchema = useMemo(() => createContactSchema(contactContent.validation), [contactContent.validation]);

  const defaultValues = useMemo<Partial<ContactFormValues>>(
    () => ({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    }),
    [],
  );

  const { heroHeading, heroSubheading, fields, placeholders, validation, submission } = contactContent;

  const notProvided = submission.notProvided ?? 'Not provided';
  const subjectTemplate = submission.subjectTemplate ?? 'New enquiry from {name}';

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    mode: 'onSubmit',
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    setError,
    clearErrors,
  } = form;

  const onSubmit = async (values: ContactFormValues) => {
    if (!isRecaptchaConfigured) {
      setError('message', {
        type: 'manual',
        message:
          submission.captchaMissing ??
          'Our spam protection is temporarily unavailable. Please call us or email directly.',
      });
      return;
    }

    const captcha = window.grecaptcha?.getResponse();
    if (!captcha) {
      setError('message', { type: 'manual', message: validation.captcha });
      return;
    }

    try {
      clearErrors('message');

      const response = await fetch(FORM_SUBMISSION_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          phone: values.phone || notProvided,
          subject: values.subject,
          message: values.message,
          _subject: subjectTemplate.replace('{name}', values.name),
          _template: 'table',
          'g-recaptcha-response': captcha,
        }),
      });

      if (!response.ok) {
        throw new Error(`Form submission failed with status ${response.status}`);
      }

      window.grecaptcha?.reset?.();

      reset(defaultValues, { keepIsSubmitSuccessful: true });
    } catch (error) {
      console.error('Unable to forward contact form submission', error);
      setError('message', {
        type: 'manual',
        message: submission.error,
      });
    }
  };

  const restaurantName = t('footer.restaurantName', 'CheckPoint Charly') as string;
  const addressLine1 = t('footer.addressLine1', 'Hristo Belchev 61, Sofia 1000, Bulgaria') as string;
  const country = t('footer.addressLine2', 'Sofia, Bulgaria') as string;
  const mobileNumber = t('footer.mobile', '+359 889 123 456') as string;
  const landlineNumber = t('footer.landline', '+359 2 123 4567') as string;
  const mobileLabel = t('contact.info.mobileLabel', 'Mobile') as string;
  const landlineLabel = t('contact.info.landlineLabel', 'Stationary') as string;
  const emailLabel = t('footer.emailLabel', 'Email') as string;
  const contactEmail = t('footer.email', 'contact@checkpointcharly.bg') as string;
  const followUs = t('footer.followUs', 'Follow Us') as string;
  const facebookAria = t('footer.facebookAria', 'Visit CheckPoint Charly on Facebook') as string;
  const tripadvisorAria = t('footer.tripadvisorAria', 'Visit CheckPoint Charly on Tripadvisor') as string;
  const shortDescription = t(
    'contact.info.description',
    'A beloved jazz restaurant in the heart of Sofia blending refined dining with live performances since 2006.',
  ) as string;

  const directionsLabel = t('contact.info.directions', 'Get directions') as string;
  const callUsLabel = t('contact.info.callUs', 'Call us') as string;
  const emailUsLabel = t('contact.info.emailUs', 'Send a message') as string;
  const emailPlaceholder = t('contact.placeholders.emailAddress', 'Your email address') as string;
  const mapExternalUrl =
    'https://www.google.com/maps/place/Checkpoint+Charly/@42.692953,23.3251032,834m/data=!3m2!1e3!4b1!4m6!3m5!1s0x40aa8573af559df5:0xa2cb552d3a80ba92!8m2!3d42.6929491!4d23.3276781!16s%2Fg%2F1tjt0f89?hl=en&entry=ttu&g_ep=EgoyMDI1MTAyMi4wIKXMDSoASAFQAw%3D%3D';
  const mapEmbedUrl =
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2932.722650743332!2d23.3276781!3d42.6929491!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40aa8573af559df5%3A0xa2cb552d3a80ba92!2sCheckpoint%20Charly!5e0!3m2!1sen!2sbg!4v1730148900000!5m2!1sen!2sbg';

  return (
    <section className="relative isolate overflow-hidden px-6 pb-10 pt-16 sm:px-10 sm:pb-12">
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <img
          src={saltBg}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(9,12,15,0.56)_0%,rgba(9,12,15,0.4)_45%,rgba(9,12,15,0.28)_100%)]" aria-hidden="true" />
      </div>

      <div className="relative mx-auto max-w-6xl space-y-16">
        <header className="space-y-4 text-center">
          <h1 className="text-4xl font-heading text-[var(--color-text-primary)]">
            <span className="heading-accent">{heroHeading}</span>
          </h1>
          <p className="mx-auto max-w-2xl text-base text-[var(--color-text-primary)]/75">{heroSubheading}</p>
        </header>

        <div className="grid gap-12 lg:grid-cols-2">
          <div className="space-y-10">
            <div className="max-w-xl space-y-3">
              <p className="text-sm uppercase tracking-[0.32em] text-[var(--color-accent)]">{t('contact.info.heading', 'Visit us')}</p>
              <p className="text-lg leading-relaxed text-[var(--color-text-muted)]">{shortDescription}</p>
            </div>

            <div className="grid gap-10 sm:grid-cols-2">
              <div className="space-y-3">
                <span className="text-xs uppercase tracking-[0.32em] text-white/60">
                  {t('contact.info.addressHeading', 'Our Address')}
                </span>
                <p className="text-sm leading-relaxed text-[var(--color-text-muted)]">
                  <strong className="block text-white/90">{restaurantName}</strong>
                  {addressLine1}
                  <br />
                  {country}
                </p>
                <a
                  href={mapExternalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-accent)] transition hover:text-[var(--color-accent-hover)]"
                >
                  {directionsLabel}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                    <path d="M10.75 3a.75.75 0 0 0-1.5 0v6.25H3a.75.75 0 0 0 0 1.5h7.75V17a.75.75 0 0 0 1.28.53l6-6a.75.75 0 0 0 0-1.06l-6-6A.75.75 0 0 0 10.75 3z" />
                  </svg>
                </a>
              </div>

              <div className="space-y-3">
                <span className="text-xs uppercase tracking-[0.32em] text-white/60">
                  {t('contact.info.phoneHeading', 'Our Phone')}
                </span>
                <p className="text-sm leading-relaxed text-[var(--color-text-muted)]">
                  {mobileLabel}:{' '}
                  <a href={`tel:${mobileNumber}`} className="hover:text-[var(--color-accent)]">
                    {mobileNumber}
                  </a>
                  <br />
                  {landlineLabel}:{' '}
                  <a href={`tel:${landlineNumber}`} className="hover:text-[var(--color-accent)]">
                    {landlineNumber}
                  </a>
                </p>
                <a
                  href={`tel:${mobileNumber}`}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-accent)] transition hover:text-[var(--color-accent-hover)]"
                >
                  {callUsLabel}
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.6" stroke="currentColor" className="h-4 w-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 4.5l1.5-1.5a1.125 1.125 0 0 1 1.59 0l2.122 2.122a1.125 1.125 0 0 1 0 1.59L6.56 8.61c.497 1.2 1.22 2.31 2.165 3.255a11.954 11.954 0 0 0 3.255 2.165l1.898-1.898a1.125 1.125 0 0 1 1.59 0l2.122 2.121a1.125 1.125 0 0 1 0 1.59l-1.5 1.5c-.44.44-1.051.63-1.652.5-2.455-.534-4.78-1.84-6.734-3.794C6.09 12.56 4.784 10.235 4.25 7.78c-.13-.6.06-1.212.5-1.652z" />
                  </svg>
                </a>
              </div>

              <div className="space-y-3">
                <span className="text-xs uppercase tracking-[0.32em] text-white/60">
                  {t('contact.info.emailHeading', 'Our Email')}
                </span>
                <p className="text-sm leading-relaxed text-[var(--color-text-muted)]">
                  {emailLabel}
                  <br />
                  <a href={`mailto:${contactEmail}`} className="hover:text-[var(--color-accent)]">
                    {contactEmail}
                  </a>
                </p>
                <NavLink
                  to="/contact#contact-form"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-accent)] transition hover:text-[var(--color-accent-hover)]"
                >
                  {emailUsLabel}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 6l8 7 8-7M4 6v12h16V6" />
                  </svg>
                </NavLink>
              </div>

              <div className="space-y-3">
                <span className="text-xs uppercase tracking-[0.32em] text-white/60">{followUs}</span>
                <div className="flex items-center gap-3">
                  <a
                    href="https://www.facebook.com/checkpoint.charly.9"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={facebookAria}
                    className="transition hover:opacity-80"
                  >
                    <img src={facebookIcon} alt="Facebook" className="h-10 w-10" />
                  </a>
                  <a
                    href="https://www.tripadvisor.com/Restaurant_Review-g294452-d1481065-Reviews-Checkpoint_Charly-Sofia_Sofia_Region.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={tripadvisorAria}
                    className="transition hover:opacity-80"
                  >
                    <img src={tripadvisorIcon} alt="Tripadvisor" className="h-10 w-10" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-[36px] shadow-[0_40px_120px_-60px_rgba(0,0,0,0.8)]">
            <iframe
              src={mapEmbedUrl}
              title={t('contact.info.mapTitle', 'Map to CheckPoint Charly') as string}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              className="h-[320px] w-full border-0 md:h-[100%]"
            />
          </div>
        </div>

        <div className="h-px w-full bg-white/10" aria-hidden="true" />

        <form
          id="contact-form"
          onSubmit={handleSubmit(onSubmit)}
          className="glass-surface glass-section mx-auto max-w-3xl space-y-8"
        >
          <div className="grid gap-6 md:grid-cols-2">
            <label className="flex flex-col gap-2 text-sm">
              <span className="font-semibold text-[var(--color-text-primary)]">{fields.name}</span>
              <input
                {...register('name')}
                type="text"
                placeholder={placeholders.name}
                className="glass-input focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen"
              />
              {errors.name && <span className="text-xs text-rose-500">{errors.name.message}</span>}
            </label>

            <label className="flex flex-col gap-2 text-sm">
              <span className="font-semibold text-[var(--color-text-primary)]">{fields.email}</span>
              <input
                {...register('email')}
                type="email"
                placeholder={emailPlaceholder}
                className="glass-input focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen"
              />
              {errors.email && <span className="text-xs text-rose-500">{errors.email.message}</span>}
            </label>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <label className="flex flex-col gap-2 text-sm">
              <span className="font-semibold text-[var(--color-text-primary)]">{fields.phone}</span>
              <input
                {...register('phone')}
                type="tel"
                placeholder={placeholders.phone}
                className="glass-input focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen"
              />
              {errors.phone && <span className="text-xs text-rose-500">{errors.phone.message}</span>}
            </label>

            <label className="flex flex-col gap-2 text-sm">
              <span className="font-semibold text-[var(--color-text-primary)]">{fields.subject}</span>
              <input
                {...register('subject')}
                type="text"
                placeholder={placeholders.subject ?? fields.subject}
                className="glass-input focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen"
              />
              {errors.subject && <span className="text-xs text-rose-500">{errors.subject.message}</span>}
            </label>
          </div>

          <label className="flex flex-col gap-2 text-sm">
            <span className="font-semibold text-[var(--color-text-primary)]">{fields.message}</span>
            <textarea
              {...register('message')}
              rows={6}
              placeholder={placeholders.message}
              className="glass-input focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen"
            />
            {errors.message && <span className="text-xs text-rose-500">{errors.message.message}</span>}
          </label>

          {isRecaptchaConfigured ? (
            <div className="flex justify-center">
              <ReCAPTCHA sitekey={recaptchaSiteKey} />
            </div>
          ) : (
            <p className="text-center text-xs text-amber-400">
              {submission.captchaMissing ?? 'Spam protection is temporarily unavailable. Please call us or email directly.'}
            </p>
          )}

          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="glass-button glass-button--accent inline-flex items-center justify-center px-6 py-3 text-sm uppercase tracking-[0.18em] disabled:opacity-70"
            >
              {isSubmitting ? submission.sending : submission.cta}
            </button>
          </div>
          {isSubmitSuccessful ? (
            <p className="text-center text-sm text-emerald-400">{submission.success}</p>
          ) : null}
        </form>
      </div>
    </section>
  );
}
